import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import advancedSimulationApi, {
  getAdvancedSimulationClientPersonas,
  getAdvancedSimulationTherapistPersonas,
  getReviewerPersonas
} from '../../../services/advancedSimulationApi';
import TranscriptDisplay from '../TranscriptDisplay';
import SimulationAudioControls from './SimulationAudioControls';
import AdvancedSimulationExportModal from './AdvancedSimulationExportModal';
import CBCMetricsDisplay from './CBCMetricsDisplay';
import ParasocialMetricsDisplay from './ParasocialMetricsDisplay';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  Paper,
  Button,
  Typography,
  Box,
  CircularProgress,
  Card,
  CardContent,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Grid,
  Divider,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Collapse,
  Autocomplete,
  Tooltip
} from '@mui/material';
import {
  Download as DownloadIcon,
  ExpandMore as ExpandMoreIcon,
  Person as PersonIcon,
  Psychology as PsychologyIcon,
  Settings as SettingsIcon,
  Timer as TimerIcon,
  Message as MessageIcon,
  Refresh as RefreshIcon,
  Security as SecurityIcon,
  Memory as MemoryIcon,
  Tune as TuneIcon,
  FilterList as FilterListIcon,
  Clear as ClearIcon
} from '@mui/icons-material';

const AdvancedSimulationViewer = ({ token, isResearcher = false }) => {
  const navigate = useNavigate();
  const [runs, setRuns] = useState([]);
  const [selectedRun, setSelectedRun] = useState(null);
  const [lastViewedRunId, setLastViewedRunId] = useState(null);
  const [isLoadingList, setIsLoadingList] = useState(false);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);
  const [error, setError] = useState('');

  // State for review and summary
  const [review, setReview] = useState(null);
  const [summary, setSummary] = useState(null);
  const [isReviewLoading, setIsReviewLoading] = useState(false);
  const [isSummaryLoading, setIsSummaryLoading] = useState(false);
  const [reviewError, setReviewError] = useState('');
  const [summaryError, setSummaryError] = useState('');
  const [reviewerPersonas, setReviewerPersonas] = useState([]);
  const [selectedReviewerPersonaId, setSelectedReviewerPersonaId] = useState('');

  // State for export modal
  const [exportModalOpen, setExportModalOpen] = useState(false);

  // Filter, pagination, and sort state
  const [filters, setFilters] = useState({
    status: '',
    runType: '',
    clientPersonaKey: '',
    therapistPersonaKey: '',
    startDate: '',
    endDate: '',
    completionReason: '',
    minTurns: '',
    maxTurns: ''
  });
  const [page, setPage] = useState(0); // MUI TablePagination is 0-indexed
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState('desc');
  const [paginationInfo, setPaginationInfo] = useState({ total: 0, totalPages: 0 });
  const [filtersOpen, setFiltersOpen] = useState(false);

  // Persona options for filter dropdowns
  const [clientPersonas, setClientPersonas] = useState([]);
  const [therapistPersonas, setTherapistPersonas] = useState([]);

  useEffect(() => {
    if (!token) return;
    const loadPersonas = async () => {
      try {
        const [clientRes, therapistRes, reviewerRes] = await Promise.all([
          getAdvancedSimulationClientPersonas(token),
          getAdvancedSimulationTherapistPersonas(token),
          getReviewerPersonas(token)
        ]);
        setClientPersonas(clientRes.personas || []);
        setTherapistPersonas(therapistRes.personas || []);
        setReviewerPersonas(reviewerRes.personas || []);
      } catch (err) {
        console.error('Failed to load personas for filters:', err);
      }
    };
    loadPersonas();
  }, [token]);

  const clientPersonaOptions = useMemo(() =>
    clientPersonas.map(p => ({ key: p.key, label: `${p.name} (${p.key})` })),
    [clientPersonas]
  );

  const therapistPersonaOptions = useMemo(() =>
    therapistPersonas.map(p => ({ key: p.key, label: `${p.name} (${p.key})` })),
    [therapistPersonas]
  );

  const fetchRunsList = useCallback(async () => {
    if (!token) return;
    setIsLoadingList(true);
    setError('');
    setReview(null);
    setSummary(null);
    setReviewError('');
    setSummaryError('');
    try {
      const queryParams = {
        page: page + 1, // API is 1-indexed
        limit: rowsPerPage,
        sortBy,
        sortOrder,
        ...filters
      };
      const data = await advancedSimulationApi.listAdvancedSimulationRuns(queryParams, token);
      setRuns(data.runs || []);
      if (data.pagination) {
        setPaginationInfo(data.pagination);
      }
    } catch (err) {
      setError('Failed to load simulation history.');
      console.error(err);
    } finally {
      setIsLoadingList(false);
    }
  }, [token, page, rowsPerPage, sortBy, sortOrder, filters]);

  useEffect(() => {
    if (!selectedRun) {
      fetchRunsList();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, page, rowsPerPage, sortBy, sortOrder]);

  const handleSelectRun = async (runId) => {
    if (!token || !runId) return;
    if (selectedRun && selectedRun._id === runId) {
      return;
    }

    setIsLoadingDetails(true);
    setError('');
    setReview(null);
    setSummary(null);
    setReviewError('');
    setSummaryError('');

    try {
      const data = await advancedSimulationApi.getAdvancedSimulationRun(runId, token);
      setSelectedRun(data);
      setLastViewedRunId(runId);
      if (data.review) setReview(data.review);
      if (data.aiSummary) setSummary(data.aiSummary);
    } catch (err) {
      setError(`Failed to load details for run ${runId}.`);
      setSelectedRun(null);
      console.error(err);
    } finally {
      setIsLoadingDetails(false);
    }
  };

  const handleRequestReview = async () => {
    if (!selectedRun || !token) return;
    setIsReviewLoading(true);
    setReviewError('');
    try {
      const result = await advancedSimulationApi.generateRunReview(
        selectedRun._id,
        token,
        selectedReviewerPersonaId || null
      );
      setReview(result.review);
      setSelectedRun(prev => ({...prev, review: result.review}));
    } catch (err) {
      setReviewError(`Failed to request review: ${err.message || 'Unknown error'}`);
      console.error(err);
    } finally {
      setIsReviewLoading(false);
    }
  };

  const handleGenerateSummary = async () => {
    if (!selectedRun || !token) return;
    setIsSummaryLoading(true);
    setSummaryError('');
    try {
      const result = await advancedSimulationApi.generateRunSummary(selectedRun._id, token);
      setSummary(result.summary);
      setSelectedRun(prev => ({...prev, aiSummary: result.summary}));
    } catch (err) {
      setSummaryError(`Failed to generate summary: ${err.message || 'Unknown error'}`);
      console.error(err);
    } finally {
      setIsSummaryLoading(false);
    }
  };

  const handleSortChange = (column) => {
    if (sortBy === column) {
      setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('desc');
    }
    setPage(0);
  };

  const handleApplyFilters = () => {
    setPage(0);
    fetchRunsList();
  };

  const handleClearFilters = () => {
    setFilters({
      status: '',
      runType: '',
      clientPersonaKey: '',
      therapistPersonaKey: '',
      startDate: '',
      endDate: '',
      completionReason: '',
      minTurns: '',
      maxTurns: ''
    });
    setPage(0);
    // fetchRunsList will be triggered by the page change useEffect
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleBackToList = () => {
    setSelectedRun(null);
    setReview(null);
    setSummary(null);
    if (runs.length === 0 && !isLoadingList) {
      fetchRunsList();
    }
  };

  const handleExportRun = () => {
    if (selectedRun) {
      setExportModalOpen(true);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  // Convert turns to messages format for TranscriptDisplay
  const convertTurnsToMessages = (turns) => {
    if (!turns || !Array.isArray(turns)) return [];
    return turns.map((turn, idx) => ({
      sender: turn.actor,
      text: turn.content,
      timestamp: turn.timestamp,
      turnNumber: idx + 1,
      gen2Trace: turn.metadata?.gen2Trace || null
    }));
  };

  // Render value with object handling
  const renderValue = (value) => {
    if (value === null || value === undefined) return 'N/A';
    if (typeof value === 'object') {
      return JSON.stringify(value);
    }
    return value;
  };

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h5" gutterBottom>Advanced Simulation History</Typography>

      {error && <Typography color="error" sx={{ mb: 2 }}>{error}</Typography>}

      {selectedRun ? (
        // Detail View
        <>
          {/* Sticky Action Bar */}
          <Paper
            elevation={2}
            sx={{
              position: 'sticky',
              top: 0,
              zIndex: 100,
              p: 2,
              mb: 2,
              backgroundColor: 'background.paper',
              borderRadius: 1
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Button onClick={handleBackToList} variant="outlined">
                  &larr; Back to List
                </Button>
              </Box>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                <FormControl size="small" sx={{ minWidth: 180 }}>
                  <InputLabel>Reviewer</InputLabel>
                  <Select
                    value={selectedReviewerPersonaId}
                    onChange={(e) => setSelectedReviewerPersonaId(e.target.value)}
                    label="Reviewer"
                    disabled={isReviewLoading}
                  >
                    <MenuItem value="">Default Review Prompt</MenuItem>
                    {reviewerPersonas.map(p => (
                      <MenuItem key={p._id} value={p._id}>{p.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Button
                  onClick={handleRequestReview}
                  disabled={isReviewLoading}
                  variant={review?.evaluation ? "outlined" : "contained"}
                  size="medium"
                  color={review?.evaluation ? "success" : "primary"}
                >
                  {isReviewLoading ? 'Generating Review...' : (review?.evaluation ? 'Regenerate Review' : 'Generate Review')}
                </Button>
                <Button
                  onClick={handleGenerateSummary}
                  disabled={isSummaryLoading}
                  variant={(summary?.therapistPerspective && summary?.clientPerspective) ? "outlined" : "contained"}
                  size="medium"
                  color={(summary?.therapistPerspective && summary?.clientPerspective) ? "success" : "primary"}
                >
                  {isSummaryLoading ? 'Generating Summaries...' :
                   ((summary?.therapistPerspective && summary?.clientPerspective) ? 'Regenerate Summaries' : 'Generate Summaries')}
                </Button>
                <Button
                  onClick={handleExportRun}
                  variant="outlined"
                  size="medium"
                  startIcon={<DownloadIcon />}
                >
                  Export
                </Button>
              </Box>
            </Box>
          </Paper>

          <Paper elevation={3} sx={{ p: 2 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>Run Details - {formatDate(selectedRun.createdAt)}</Typography>

          {/* Configuration Section */}
          <Accordion defaultExpanded sx={{ mb: 2 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <SettingsIcon color="action" />
                <Typography variant="subtitle1" fontWeight="bold">Configuration & Metrics</Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Run ID</Typography>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                      {selectedRun.runId?.substring(0, 12)}...
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Status</Typography>
                    <Typography variant="body2">
                      <Chip label={selectedRun.status} size="small" color={
                        selectedRun.status === 'completed' ? 'success' :
                        selectedRun.status === 'failed' ? 'error' :
                        selectedRun.status === 'running' ? 'primary' : 'default'
                      } />
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Created At</Typography>
                    <Typography variant="body2">{formatDate(selectedRun.createdAt)}</Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <MessageIcon fontSize="small" color="action" />
                    <Box>
                      <Typography variant="caption" color="text.secondary">Messages</Typography>
                      <Typography variant="body2">{selectedRun.turns?.length || 0}</Typography>
                    </Box>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <TimerIcon fontSize="small" color="action" />
                    <Box>
                      <Typography variant="caption" color="text.secondary">Duration</Typography>
                      <Typography variant="body2">
                        {selectedRun.startedAt && selectedRun.completedAt
                          ? `${Math.round((new Date(selectedRun.completedAt) - new Date(selectedRun.startedAt)) / 1000 / 60)} min`
                          : 'N/A'}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <TimerIcon fontSize="small" color="action" />
                    <Box>
                      <Typography variant="caption" color="text.secondary">Est. Real-World Duration</Typography>
                      <Typography variant="body2">
                        {selectedRun.metrics?.estimatedRealWorldDurationS
                          ? `~${Math.round(selectedRun.metrics.estimatedRealWorldDurationS / 60)} min`
                          : 'N/A'}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Goals Achieved</Typography>
                    <Typography variant="body2">
                      {renderValue(selectedRun.metrics?.goalsAchieved)}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Total Tokens</Typography>
                    <Typography variant="body2">
                      {selectedRun.metrics?.totalTokens?.toLocaleString() || 0}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Estimated Cost</Typography>
                    <Typography variant="body2">
                      ${(selectedRun.metrics?.estimatedCost || 0).toFixed(4)}
                    </Typography>
                  </Box>
                </Grid>
                {selectedRun.completionReason && (
                  <Grid item xs={12}>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Completion Reason</Typography>
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', fontSize: '0.8rem' }}>
                        {renderValue(selectedRun.completionReason)}
                      </Typography>
                    </Box>
                  </Grid>
                )}
              </Grid>
            </AccordionDetails>
          </Accordion>

          {/* Simulation Configuration Section - LLM, CBC, Parasocial Settings */}
          <Accordion sx={{ mb: 2 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TuneIcon color="action" />
                <Typography variant="subtitle1" fontWeight="bold">Simulation Configuration</Typography>
                <Box sx={{ display: 'flex', gap: 0.5, ml: 'auto' }}>
                  {selectedRun.configSnapshot?.cbcConfig?.enabled && (
                    <Chip label="CBC" size="small" color="primary" variant="outlined" />
                  )}
                  {selectedRun.configSnapshot?.parasocialConfig?.enabled && (
                    <Chip label="Parasocial" size="small" color="secondary" variant="outlined" />
                  )}
                </Box>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={3}>
                {/* LLM Configuration */}
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <MemoryIcon fontSize="small" color="primary" />
                    <Typography variant="subtitle2" fontWeight="bold">LLM Configuration</Typography>
                  </Box>
                  <Grid container spacing={2}>
                    {/* Client LLM */}
                    <Grid item xs={12} md={6}>
                      <Paper variant="outlined" sx={{ p: 2 }}>
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                          Client (Simulated User)
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="body2" color="text.secondary">Provider:</Typography>
                            <Chip
                              label={selectedRun.configSnapshot?.clientLlmConfig?.provider || 'openai'}
                              size="small"
                              color="default"
                            />
                          </Box>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="body2" color="text.secondary">Model:</Typography>
                            <Typography variant="body2" fontWeight="medium">
                              {selectedRun.configSnapshot?.clientLlmConfig?.model || 'gpt-4o-mini'}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="body2" color="text.secondary">Temperature:</Typography>
                            <Typography variant="body2">
                              {selectedRun.configSnapshot?.clientLlmConfig?.temperature ?? 0.7}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="body2" color="text.secondary">Max Tokens:</Typography>
                            <Typography variant="body2">
                              {selectedRun.configSnapshot?.clientLlmConfig?.maxTokens || 2048}
                            </Typography>
                          </Box>
                        </Box>
                      </Paper>
                    </Grid>
                    {/* Therapist LLM */}
                    <Grid item xs={12} md={6}>
                      <Paper variant="outlined" sx={{ p: 2 }}>
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                          Therapist (AI Response)
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="body2" color="text.secondary">Provider:</Typography>
                            <Chip
                              label={selectedRun.configSnapshot?.therapistLlmConfig?.provider || 'vertex_ai'}
                              size="small"
                              color="primary"
                              variant="outlined"
                            />
                          </Box>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="body2" color="text.secondary">Model:</Typography>
                            <Typography variant="body2" fontWeight="medium">
                              {selectedRun.configSnapshot?.therapistLlmConfig?.model || 'gemini-2.5-flash'}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="body2" color="text.secondary">Temperature:</Typography>
                            <Typography variant="body2">
                              {selectedRun.configSnapshot?.therapistLlmConfig?.temperature ?? 0.7}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="body2" color="text.secondary">Max Tokens:</Typography>
                            <Typography variant="body2">
                              {selectedRun.configSnapshot?.therapistLlmConfig?.maxTokens || 2048}
                            </Typography>
                          </Box>
                        </Box>
                      </Paper>
                    </Grid>
                  </Grid>
                </Grid>

                <Grid item xs={12}>
                  <Divider />
                </Grid>

                {/* CBC Configuration */}
                <Grid item xs={12} md={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <SecurityIcon fontSize="small" color={selectedRun.configSnapshot?.cbcConfig?.enabled ? 'primary' : 'disabled'} />
                    <Typography variant="subtitle2" fontWeight="bold">
                      CBC (Casual Boundary Crossing)
                    </Typography>
                    <Chip
                      label={selectedRun.configSnapshot?.cbcConfig?.enabled ? 'Enabled' : 'Disabled'}
                      size="small"
                      color={selectedRun.configSnapshot?.cbcConfig?.enabled ? 'success' : 'default'}
                    />
                  </Box>
                  {selectedRun.configSnapshot?.cbcConfig?.enabled ? (
                    <Paper variant="outlined" sx={{ p: 2 }}>
                      <Grid container spacing={1}>
                        <Grid item xs={6}>
                          <Typography variant="caption" color="text.secondary">Preset:</Typography>
                          <Typography variant="body2" fontWeight="medium">
                            {selectedRun.configSnapshot?.cbcConfig?.preset || 'moderate'}
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="caption" color="text.secondary">Classification:</Typography>
                          <Typography variant="body2">
                            {selectedRun.configSnapshot?.cbcConfig?.classificationMethod || 'inline_prompt'}
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="caption" color="text.secondary">Transparency:</Typography>
                          <Typography variant="body2">
                            {selectedRun.configSnapshot?.cbcConfig?.transparencyMode || 'implicit_then_explicit'}
                          </Typography>
                        </Grid>
                        {selectedRun.configSnapshot?.cbcConfig?.thresholds && (
                          <>
                            <Grid item xs={12}>
                              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                                Thresholds:
                              </Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <Typography variant="caption" color="text.secondary">Observation:</Typography>
                              <Typography variant="body2">
                                {selectedRun.configSnapshot?.cbcConfig?.thresholds?.observationThreshold ?? 10}
                              </Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <Typography variant="caption" color="text.secondary">Soft Reconnection:</Typography>
                              <Typography variant="body2">
                                {selectedRun.configSnapshot?.cbcConfig?.thresholds?.softReconnectionThreshold ?? 15}
                              </Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <Typography variant="caption" color="text.secondary">Explicit Reconnection:</Typography>
                              <Typography variant="body2">
                                {selectedRun.configSnapshot?.cbcConfig?.thresholds?.explicitReconnectionThreshold ?? 20}
                              </Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <Typography variant="caption" color="text.secondary">Alert:</Typography>
                              <Typography variant="body2">
                                {selectedRun.configSnapshot?.cbcConfig?.thresholds?.alertThreshold ?? 30}
                              </Typography>
                            </Grid>
                          </>
                        )}
                      </Grid>
                    </Paper>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      CBC detection was not enabled for this simulation run.
                    </Typography>
                  )}
                </Grid>

                {/* Parasocial Configuration */}
                <Grid item xs={12} md={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <SecurityIcon fontSize="small" color={selectedRun.configSnapshot?.parasocialConfig?.enabled ? 'secondary' : 'disabled'} />
                    <Typography variant="subtitle2" fontWeight="bold">
                      Parasocial Detection
                    </Typography>
                    <Chip
                      label={selectedRun.configSnapshot?.parasocialConfig?.enabled ? 'Enabled' : 'Disabled'}
                      size="small"
                      color={selectedRun.configSnapshot?.parasocialConfig?.enabled ? 'success' : 'default'}
                    />
                  </Box>
                  {selectedRun.configSnapshot?.parasocialConfig?.enabled ? (
                    <Paper variant="outlined" sx={{ p: 2 }}>
                      <Grid container spacing={1}>
                        <Grid item xs={6}>
                          <Typography variant="caption" color="text.secondary">Disclosure Mode:</Typography>
                          <Typography variant="body2" fontWeight="medium">
                            {selectedRun.configSnapshot?.parasocialConfig?.disclosureMode || 'moderate'}
                          </Typography>
                        </Grid>
                        {selectedRun.configSnapshot?.parasocialConfig?.thresholds && (
                          <>
                            <Grid item xs={12}>
                              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                                Thresholds:
                              </Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <Typography variant="caption" color="text.secondary">Soft Disclosure:</Typography>
                              <Typography variant="body2">
                                {selectedRun.configSnapshot?.parasocialConfig?.thresholds?.softDisclosureThreshold ?? 20}
                              </Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <Typography variant="caption" color="text.secondary">Active Redirect:</Typography>
                              <Typography variant="body2">
                                {selectedRun.configSnapshot?.parasocialConfig?.thresholds?.activeRedirectThreshold ?? 40}
                              </Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <Typography variant="caption" color="text.secondary">Explicit Disclosure:</Typography>
                              <Typography variant="body2">
                                {selectedRun.configSnapshot?.parasocialConfig?.thresholds?.explicitDisclosureThreshold ?? 60}
                              </Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <Typography variant="caption" color="text.secondary">Alert:</Typography>
                              <Typography variant="body2">
                                {selectedRun.configSnapshot?.parasocialConfig?.thresholds?.alertThreshold ?? 80}
                              </Typography>
                            </Grid>
                          </>
                        )}
                        {selectedRun.configSnapshot?.parasocialConfig?.interventionsEnabled && (
                          <>
                            <Grid item xs={12}>
                              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                                Interventions Enabled:
                              </Typography>
                            </Grid>
                            <Grid item xs={12}>
                              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                {selectedRun.configSnapshot?.parasocialConfig?.interventionsEnabled?.softDisclosure && (
                                  <Chip label="Soft Disclosure" size="small" variant="outlined" />
                                )}
                                {selectedRun.configSnapshot?.parasocialConfig?.interventionsEnabled?.activeRedirect && (
                                  <Chip label="Active Redirect" size="small" variant="outlined" />
                                )}
                                {selectedRun.configSnapshot?.parasocialConfig?.interventionsEnabled?.explicitDisclosure && (
                                  <Chip label="Explicit Disclosure" size="small" variant="outlined" />
                                )}
                              </Box>
                            </Grid>
                          </>
                        )}
                      </Grid>
                    </Paper>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      Parasocial detection was not enabled for this simulation run.
                    </Typography>
                  )}
                </Grid>

                {/* Max Turns Setting */}
                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                  <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Max Turns:</Typography>
                      <Typography variant="body2">
                        {selectedRun.configSnapshot?.maxTurns || 50}
                      </Typography>
                    </Box>
                    {selectedRun.configSnapshot?.randomSeed && (
                      <Box>
                        <Typography variant="caption" color="text.secondary">Random Seed:</Typography>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                          {selectedRun.configSnapshot?.randomSeed}
                        </Typography>
                      </Box>
                    )}
                    {selectedRun.configSnapshot?.promptVariation && (
                      <Box>
                        <Typography variant="caption" color="text.secondary">Prompt Variation:</Typography>
                        <Typography variant="body2">
                          {selectedRun.configSnapshot?.promptVariation}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>

          {/* Client Persona Details */}
          {selectedRun.configSnapshot?.clientPersonaSnapshot && (
            <Accordion sx={{ mb: 2 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PersonIcon color="action" />
                  <Typography variant="subtitle1" fontWeight="bold">
                    Client Persona: {selectedRun.configSnapshot.clientPersonaSnapshot.name}
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary">Description</Typography>
                    <Typography variant="body2" paragraph>
                      {selectedRun.configSnapshot.clientPersonaSnapshot.description}
                    </Typography>
                  </Grid>

                  {selectedRun.configSnapshot.clientPersonaSnapshot.openingLine && (
                    <Grid item xs={12}>
                      <Typography variant="caption" color="text.secondary">Opening Line</Typography>
                      <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                        "{selectedRun.configSnapshot.clientPersonaSnapshot.openingLine}"
                      </Typography>
                    </Grid>
                  )}

                  {selectedRun.configSnapshot.clientPersonaSnapshot.background && (
                    <Grid item xs={12}>
                      <Typography variant="caption" color="text.secondary">Background</Typography>
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                        {selectedRun.configSnapshot.clientPersonaSnapshot.background}
                      </Typography>
                    </Grid>
                  )}

                  {selectedRun.configSnapshot.clientPersonaSnapshot.presentingProblems && (
                    <Grid item xs={12}>
                      <Typography variant="caption" color="text.secondary">Presenting Problems</Typography>
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                        {selectedRun.configSnapshot.clientPersonaSnapshot.presentingProblems}
                      </Typography>
                    </Grid>
                  )}

                  {selectedRun.configSnapshot.clientPersonaSnapshot.sessionBehaviour && (
                    <Grid item xs={12}>
                      <Typography variant="caption" color="text.secondary">Session Behaviour</Typography>
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                        {selectedRun.configSnapshot.clientPersonaSnapshot.sessionBehaviour}
                      </Typography>
                    </Grid>
                  )}

                  {selectedRun.configSnapshot.clientPersonaSnapshot.therapeuticDynamics && (
                    <Grid item xs={12}>
                      <Typography variant="caption" color="text.secondary">Therapeutic Dynamics</Typography>
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                        {selectedRun.configSnapshot.clientPersonaSnapshot.therapeuticDynamics}
                      </Typography>
                    </Grid>
                  )}

                  {selectedRun.configSnapshot.clientPersonaSnapshot.demographics && (
                    <Grid item xs={12}>
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                        Demographics
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        {selectedRun.configSnapshot.clientPersonaSnapshot.demographics.ageRange && (
                          <Chip
                            label={`Age: ${selectedRun.configSnapshot.clientPersonaSnapshot.demographics.ageRange}`}
                            size="small"
                            variant="outlined"
                          />
                        )}
                        {selectedRun.configSnapshot.clientPersonaSnapshot.demographics.gender && (
                          <Chip
                            label={`Gender: ${selectedRun.configSnapshot.clientPersonaSnapshot.demographics.gender}`}
                            size="small"
                            variant="outlined"
                          />
                        )}
                        {selectedRun.configSnapshot.clientPersonaSnapshot.demographics.occupation && (
                          <Chip
                            label={`Occupation: ${selectedRun.configSnapshot.clientPersonaSnapshot.demographics.occupation}`}
                            size="small"
                            variant="outlined"
                          />
                        )}
                      </Box>
                    </Grid>
                  )}

                  {selectedRun.configSnapshot.clientPersonaSnapshot.conditions && (
                    <Grid item xs={12}>
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                        Conditions
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        {selectedRun.configSnapshot.clientPersonaSnapshot.conditions.map((condition, index) => (
                          <Chip
                            key={index}
                            label={condition}
                            size="small"
                            color="primary"
                            variant="outlined"
                          />
                        ))}
                      </Box>
                    </Grid>
                  )}

                  {selectedRun.configSnapshot.clientPersonaSnapshot.severity && (
                    <Grid item xs={12}>
                      <Typography variant="caption" color="text.secondary">Severity Level</Typography>
                      <Typography variant="body2">
                        {selectedRun.configSnapshot.clientPersonaSnapshot.severity}
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              </AccordionDetails>
            </Accordion>
          )}

          {/* Therapist Persona Details */}
          {selectedRun.configSnapshot?.therapistPersonaSnapshot && (
            <Accordion sx={{ mb: 2 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PsychologyIcon color="action" />
                  <Typography variant="subtitle1" fontWeight="bold">
                    Therapist Persona: {selectedRun.configSnapshot.therapistPersonaSnapshot.name}
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="body2">
                  {selectedRun.configSnapshot.therapistPersonaSnapshot.description}
                </Typography>
              </AccordionDetails>
            </Accordion>
          )}

          {/* Safety System Metrics (CBC and Parasocial) */}
          {(selectedRun.configSnapshot?.cbcConfig?.enabled || selectedRun.configSnapshot?.parasocialConfig?.enabled || selectedRun.metrics?.onboardingMetrics) && (
            <Accordion sx={{ mb: 2 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <SecurityIcon color="action" />
                  <Typography variant="subtitle1" fontWeight="bold">
                    Safety System Metrics
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 0.5, ml: 'auto' }}>
                    {selectedRun.configSnapshot?.cbcConfig?.enabled && (
                      <Chip label="CBC" size="small" color="primary" variant="outlined" />
                    )}
                    {selectedRun.configSnapshot?.parasocialConfig?.enabled && (
                      <Chip label="Parasocial" size="small" color="secondary" variant="outlined" />
                    )}
                  </Box>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={2}>
                  {/* CBC Metrics */}
                  {selectedRun.configSnapshot?.cbcConfig?.enabled && (
                    <Grid item xs={12}>
                      <CBCMetricsDisplay
                        cbcMetrics={selectedRun.metrics?.cbcMetrics}
                        cbcConfig={selectedRun.configSnapshot?.cbcConfig}
                        compact={false}
                      />
                    </Grid>
                  )}

                  {/* Parasocial Metrics */}
                  {selectedRun.configSnapshot?.parasocialConfig?.enabled && (
                    <Grid item xs={12}>
                      <ParasocialMetricsDisplay
                        parasocialMetrics={selectedRun.metrics?.parasocialMetrics}
                        parasocialConfig={selectedRun.configSnapshot?.parasocialConfig}
                        compact={false}
                      />
                    </Grid>
                  )}

                  {/* Onboarding Metrics */}
                  {selectedRun.metrics?.onboardingMetrics && (
                    <Grid item xs={12}>
                      <Paper variant="outlined" sx={{ p: 2 }}>
                        <Typography variant="subtitle1" gutterBottom>Onboarding Metrics</Typography>

                        {/* Stage Metrics */}
                        {selectedRun.metrics.onboardingMetrics.stageMetrics?.map((stage, i) => (
                          <Box key={i} sx={{ mb: 1, pl: 1, borderLeft: '3px solid', borderColor: 'primary.main' }}>
                            <Typography variant="body2" fontWeight="bold">
                              Stage {stage.stageNumber}: {stage.stageName}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Persona: {stage.personaKey} | {stage.turnCount} turns | {Math.round(stage.durationMs / 1000)}s
                            </Typography>
                            {stage.assessmentsCompleted?.map((a, j) => (
                              <Chip key={j} label={`${a.testKey}: ${a.score}/${a.maxScore}`}
                                size="small" sx={{ ml: 1 }} variant="outlined"
                                color={a.score > a.maxScore * 0.6 ? 'warning' : 'success'} />
                            ))}
                          </Box>
                        ))}

                        {/* Triage Result */}
                        {selectedRun.metrics.onboardingMetrics.triageResult && (
                          <Box sx={{ mt: 2, p: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}>
                            <Typography variant="subtitle2">Triage Result</Typography>
                            <Typography variant="body2">
                              Recommended: <strong>{selectedRun.metrics.onboardingMetrics.triageResult.recommendedPersonaKey}</strong>
                              {selectedRun.metrics.onboardingMetrics.triageResult.requiresReview &&
                                <Chip label="Requires Review" size="small" color="warning" sx={{ ml: 1 }} />}
                            </Typography>
                            {selectedRun.metrics.onboardingMetrics.triageResult.primaryConcern && (
                              <Typography variant="caption">
                                Primary concern: {selectedRun.metrics.onboardingMetrics.triageResult.primaryConcern}
                              </Typography>
                            )}
                          </Box>
                        )}

                        {/* Triage Accuracy */}
                        {selectedRun.metrics.onboardingMetrics.triageAccuracy && selectedRun.metrics.onboardingMetrics.triageAccuracy.personaMatch !== null && (
                          <Box sx={{ mt: 1 }}>
                            <Chip
                              label={selectedRun.metrics.onboardingMetrics.triageAccuracy.personaMatch ? 'Persona Match' : 'Persona Mismatch'}
                              size="small"
                              color={selectedRun.metrics.onboardingMetrics.triageAccuracy.personaMatch ? 'success' : 'error'}
                            />
                            <Typography variant="caption" sx={{ ml: 1 }}>
                              Expected: {selectedRun.metrics.onboardingMetrics.triageAccuracy?.expectedPersonaKey || 'N/A'}
                              {' | '}Actual: {selectedRun.metrics.onboardingMetrics.triageAccuracy?.actualPersonaKey || 'N/A'}
                            </Typography>
                          </Box>
                        )}

                        {/* Summary Stats */}
                        <Box sx={{ mt: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                          <Chip label={`Onboarding: ${selectedRun.metrics.onboardingMetrics.totalOnboardingTurns} turns`} size="small" />
                          {selectedRun.metrics.onboardingMetrics.totalTherapyTurns > 0 && (
                            <Chip label={`Therapy: ${selectedRun.metrics.onboardingMetrics.totalTherapyTurns} turns`} size="small" />
                          )}
                          <Chip
                            label={selectedRun.metrics.onboardingMetrics.flowCompletedSuccessfully ? 'Flow Complete' : 'Flow Incomplete'}
                            size="small"
                            color={selectedRun.metrics.onboardingMetrics.flowCompletedSuccessfully ? 'success' : 'warning'}
                            variant="outlined"
                          />
                        </Box>
                      </Paper>
                    </Grid>
                  )}
                </Grid>
              </AccordionDetails>
            </Accordion>
          )}

          {/* Gen2 Reasoning Summary - run level */}
          {selectedRun.gen2FinalState && (
            <>
              <Divider sx={{ my: 2 }} />
              <Accordion sx={{ mb: 2 }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PsychologyIcon color="action" />
                    <Typography variant="subtitle1" fontWeight="bold">Gen2 Reasoning Summary</Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container spacing={2}>
                    {selectedRun.gen2FinalState.modeDistribution && (
                      <Grid item xs={12} md={6}>
                        <Typography variant="caption" color="text.secondary">Mode Distribution</Typography>
                        <Typography variant="body2">
                          Fast: {selectedRun.gen2FinalState.modeDistribution.fast || 0}
                          {' | '}Deep Thinking: {selectedRun.gen2FinalState.modeDistribution.deep_thinking || 0}
                          {' | '}Consultation: {selectedRun.gen2FinalState.modeDistribution.consultation || 0}
                        </Typography>
                      </Grid>
                    )}
                    {selectedRun.gen2FinalState.modulesConsulted?.length > 0 && (
                      <Grid item xs={12} md={6}>
                        <Typography variant="caption" color="text.secondary">Modules Consulted</Typography>
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                          {selectedRun.gen2FinalState.modulesConsulted.map((mod, i) => (
                            <Chip key={i} label={mod} size="small" variant="outlined" />
                          ))}
                        </Box>
                      </Grid>
                    )}
                    {selectedRun.gen2FinalState.formulationVersion != null && (
                      <Grid item xs={12} md={6}>
                        <Typography variant="caption" color="text.secondary">Final Formulation Version</Typography>
                        <Typography variant="body2">v{selectedRun.gen2FinalState.formulationVersion}</Typography>
                      </Grid>
                    )}
                    {selectedRun.gen2FinalState.totalGen2Latency != null && (
                      <Grid item xs={12} md={6}>
                        <Typography variant="caption" color="text.secondary">Total Gen2 Latency</Typography>
                        <Typography variant="body2">{(selectedRun.gen2FinalState.totalGen2Latency / 1000).toFixed(1)}s</Typography>
                      </Grid>
                    )}
                  </Grid>
                </AccordionDetails>
              </Accordion>
            </>
          )}

          <Divider sx={{ my: 2 }} />

          {/* Transcript Section */}
          <Typography variant="h6" sx={{ mb: 2 }}>Conversation Transcript</Typography>

          {/* Audio Controls (Admin only, completed runs with turns) */}
          {!isResearcher && selectedRun?.status === 'completed' && selectedRun?.turns?.length > 0 && (
            <SimulationAudioControls
              turns={selectedRun.turns}
              runId={selectedRun.runId}
              token={token}
            />
          )}

          {isLoadingDetails && <CircularProgress size={24} />}
          {!isLoadingDetails && selectedRun.turns && (
            <TranscriptDisplay
              transcript={convertTurnsToMessages(selectedRun.turns)}
            />
          )}
          {!isLoadingDetails && !selectedRun.turns && (
            <Typography color="textSecondary">Transcript data is missing for this run.</Typography>
          )}

          {/* Review Section */}
          <Box mt={3}>
            <Typography variant="h6">Review</Typography>
            {reviewError && <Typography color="error">{reviewError}</Typography>}
            {isReviewLoading && <CircularProgress size={20} />}
            {!isReviewLoading && review && review.evaluation && (
              <Card variant="outlined" sx={{ mt: 1 }}>
                <CardContent>
                  <Typography variant="subtitle2" gutterBottom>Evaluation:</Typography>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{review.evaluation}</Typography>
                  {review.recommendations && (
                    <>
                      <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>Recommendations:</Typography>
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{review.recommendations}</Typography>
                    </>
                  )}
                  <Typography variant="caption" display="block" sx={{mt:1}}>Reviewed by: {review.reviewerPersonaName || 'Default Review Prompt'}</Typography>
                  <Typography variant="caption" display="block">Reviewed at: {formatDate(review.generatedAt)}</Typography>
                </CardContent>
              </Card>
            )}
            {!isReviewLoading && !review?.evaluation && (
              <Typography color="textSecondary" sx={{mt:1}}>No review available for this run.</Typography>
            )}
          </Box>

          {/* Summary Section */}
          <Box mt={3}>
            <Typography variant="h6">Summary</Typography>
            {summaryError && <Typography color="error">{summaryError}</Typography>}
            {isSummaryLoading && <CircularProgress size={20} />}
            {!isSummaryLoading && summary && (summary.therapistPerspective || summary.clientPerspective) && (
              <Card variant="outlined" sx={{ mt: 1 }}>
                <CardContent>
                  {summary.therapistPerspective && (
                    <>
                      <Typography variant="subtitle2" gutterBottom>
                        Therapist Perspective:
                      </Typography>
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mb: 2 }}>{summary.therapistPerspective}</Typography>
                    </>
                  )}
                  {summary.clientPerspective && (
                    <>
                      <Typography variant="subtitle2" gutterBottom>
                        Client Perspective:
                      </Typography>
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{summary.clientPerspective}</Typography>
                    </>
                  )}
                  <Typography variant="caption" display="block" sx={{mt:1}}>Summarized at: {formatDate(summary.generatedAt)}</Typography>
                </CardContent>
              </Card>
            )}
            {!isSummaryLoading && (!summary || (!summary.therapistPerspective && !summary.clientPerspective)) && (
              <Typography color="textSecondary" sx={{mt:1}}>No summary available for this run.</Typography>
            )}
          </Box>
        </Paper>
        </>
      ) : (
        // List View
        <>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="body2" color="text.secondary">
              {paginationInfo.total || runs.length} simulation{(paginationInfo.total || runs.length) !== 1 ? 's' : ''} found
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant={filtersOpen ? 'contained' : 'outlined'}
                size="small"
                startIcon={<FilterListIcon />}
                onClick={() => setFiltersOpen(prev => !prev)}
              >
                Filters
              </Button>
              <Button
                variant="outlined"
                size="small"
                startIcon={isLoadingList ? <CircularProgress size={16} /> : <RefreshIcon />}
                onClick={fetchRunsList}
                disabled={isLoadingList}
              >
                Refresh
              </Button>
            </Box>
          </Box>

          {/* Collapsible Filter Bar */}
          <Collapse in={filtersOpen}>
            <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={6} md={2}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Status</InputLabel>
                    <Select
                      value={filters.status}
                      label="Status"
                      onChange={(e) => handleFilterChange('status', e.target.value)}
                    >
                      <MenuItem value="">All</MenuItem>
                      <MenuItem value="pending">Pending</MenuItem>
                      <MenuItem value="running">Running</MenuItem>
                      <MenuItem value="completed">Completed</MenuItem>
                      <MenuItem value="failed">Failed</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Run Type</InputLabel>
                    <Select
                      value={filters.runType}
                      label="Run Type"
                      onChange={(e) => handleFilterChange('runType', e.target.value)}
                    >
                      <MenuItem value="">All</MenuItem>
                      <MenuItem value="single">Single</MenuItem>
                      <MenuItem value="batch">Batch</MenuItem>
                      <MenuItem value="cloned">Cloned</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                  <Autocomplete
                    size="small"
                    options={clientPersonaOptions}
                    getOptionLabel={(option) => typeof option === 'string' ? option : option.label}
                    value={clientPersonaOptions.find(o => o.key === filters.clientPersonaKey) || null}
                    onChange={(e, newValue) => handleFilterChange('clientPersonaKey', newValue?.key || '')}
                    isOptionEqualToValue={(option, value) => option.key === value.key}
                    renderOption={(props, option) => (
                      <li {...props} key={option.key}>
                        <Box sx={{ py: 0.25 }}>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>{option.label.split(' (')[0]}</Typography>
                          <Typography variant="caption" color="text.secondary">{option.key}</Typography>
                        </Box>
                      </li>
                    )}
                    slotProps={{ popper: { sx: { minWidth: 280 } } }}
                    renderInput={(params) => {
                      const selected = clientPersonaOptions.find(o => o.key === filters.clientPersonaKey);
                      return (
                        <Tooltip title={selected ? selected.label : ''} placement="top" arrow>
                          <TextField {...params} label="Client Persona" />
                        </Tooltip>
                      );
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                  <Autocomplete
                    size="small"
                    options={therapistPersonaOptions}
                    getOptionLabel={(option) => typeof option === 'string' ? option : option.label}
                    value={therapistPersonaOptions.find(o => o.key === filters.therapistPersonaKey) || null}
                    onChange={(e, newValue) => handleFilterChange('therapistPersonaKey', newValue?.key || '')}
                    isOptionEqualToValue={(option, value) => option.key === value.key}
                    renderOption={(props, option) => (
                      <li {...props} key={option.key}>
                        <Box sx={{ py: 0.25 }}>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>{option.label.split(' (')[0]}</Typography>
                          <Typography variant="caption" color="text.secondary">{option.key}</Typography>
                        </Box>
                      </li>
                    )}
                    slotProps={{ popper: { sx: { minWidth: 280 } } }}
                    renderInput={(params) => {
                      const selected = therapistPersonaOptions.find(o => o.key === filters.therapistPersonaKey);
                      return (
                        <Tooltip title={selected ? selected.label : ''} placement="top" arrow>
                          <TextField {...params} label="Therapist Persona" />
                        </Tooltip>
                      );
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Start Date"
                    type="date"
                    value={filters.startDate}
                    onChange={(e) => handleFilterChange('startDate', e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                  <TextField
                    fullWidth
                    size="small"
                    label="End Date"
                    type="date"
                    value={filters.endDate}
                    onChange={(e) => handleFilterChange('endDate', e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Completion Reason</InputLabel>
                    <Select
                      value={filters.completionReason}
                      label="Completion Reason"
                      onChange={(e) => handleFilterChange('completionReason', e.target.value)}
                    >
                      <MenuItem value="">All</MenuItem>
                      <MenuItem value="Natural session closure">Natural session closure</MenuItem>
                      <MenuItem value="farewell_loop_detected">Farewell loop detected</MenuItem>
                      <MenuItem value="cbc_graceful_closure">CBC graceful closure</MenuItem>
                      <MenuItem value="exceeded_turn_limit">Exceeded turn limit</MenuItem>
                      <MenuItem value="Therapeutic goals achieved">Therapeutic goals achieved</MenuItem>
                      <MenuItem value="Maximum turns reached">Maximum turns reached</MenuItem>
                      <MenuItem value="Repetitive loop detected">Repetitive loop detected</MenuItem>
                      <MenuItem value="Too many errors">Too many errors</MenuItem>
                      <MenuItem value="Token limit reached">Token limit reached</MenuItem>
                      <MenuItem value="Time limit reached">Time limit reached</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={6} sm={3} md={1}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Min Turns"
                    type="number"
                    value={filters.minTurns}
                    onChange={(e) => handleFilterChange('minTurns', e.target.value)}
                    inputProps={{ min: 0 }}
                  />
                </Grid>
                <Grid item xs={6} sm={3} md={1}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Max Turns"
                    type="number"
                    value={filters.maxTurns}
                    onChange={(e) => handleFilterChange('maxTurns', e.target.value)}
                    inputProps={{ min: 0 }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<ClearIcon />}
                      onClick={handleClearFilters}
                    >
                      Clear
                    </Button>
                    <Button
                      variant="contained"
                      size="small"
                      onClick={handleApplyFilters}
                    >
                      Apply Filters
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </Paper>
          </Collapse>

          {isLoadingList && <Typography>Loading history...</Typography>}
          {!isLoadingList && runs.length === 0 && <Typography>No simulation history found.</Typography>}
          {!isLoadingList && runs.length > 0 && (
            <TableContainer component={Paper} elevation={3}>
              <Table sx={{ minWidth: 650 }} aria-label="simulation history table">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>
                      <TableSortLabel
                        active={sortBy === 'createdAt'}
                        direction={sortBy === 'createdAt' ? sortOrder : 'desc'}
                        onClick={() => handleSortChange('createdAt')}
                      >
                        Date
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>
                      <TableSortLabel
                        active={sortBy === 'runType'}
                        direction={sortBy === 'runType' ? sortOrder : 'desc'}
                        onClick={() => handleSortChange('runType')}
                      >
                        Type
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Batch ID</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Client Persona</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Therapist Persona</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Client Model</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Therapist Model</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Safety</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>
                      <TableSortLabel
                        active={sortBy === 'turnsCount'}
                        direction={sortBy === 'turnsCount' ? sortOrder : 'desc'}
                        onClick={() => handleSortChange('turnsCount')}
                      >
                        Turns
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Est. Duration</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Completion Reason</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>
                      <TableSortLabel
                        active={sortBy === 'status'}
                        direction={sortBy === 'status' ? sortOrder : 'desc'}
                        onClick={() => handleSortChange('status')}
                      >
                        Status
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {runs.map(run => (
                    <TableRow
                      key={run._id}
                      hover
                      sx={run._id === lastViewedRunId ? { backgroundColor: 'action.hover' } : {}}
                      onClick={() => handleSelectRun(run._id)}
                      style={{ cursor: 'pointer' }}
                    >
                      <TableCell>{formatDate(run.createdAt)}</TableCell>
                      <TableCell>
                        <Chip
                          label={run.runType || 'single'}
                          size="small"
                          color={run.runType === 'batch' ? 'primary' : run.runType === 'cloned' ? 'secondary' : 'default'}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        {run.batchId ? (
                          <Typography
                            component="a"
                            variant="caption"
                            sx={{
                              fontFamily: 'monospace',
                              color: 'primary.main',
                              cursor: 'pointer',
                              textDecoration: 'underline',
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/admin/dashboard/advanced-simulation/batch/${run.batchId}`);
                            }}
                            title="Open batch progress viewer"
                          >
                            {run.batchId.toString().substring(run.batchId.toString().length - 6)}
                          </Typography>
                        ) : '-'}
                      </TableCell>
                      <TableCell>{run.configSnapshot?.clientPersonaSnapshot?.name || run.configSnapshot?.clientPersonaSnapshot?.key || run.configSnapshot?.clientPersonaKey || 'N/A'}</TableCell>
                      <TableCell>{run.configSnapshot?.therapistPersonaSnapshot?.name || run.configSnapshot?.therapistPersonaSnapshot?.key || run.configSnapshot?.therapistPersonaKey || 'N/A'}</TableCell>
                      <TableCell>
                        <Typography variant="caption" sx={{ display: 'block' }}>
                          {run.configSnapshot?.clientLlmConfig?.model || 'gpt-4o-mini'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" sx={{ display: 'block' }}>
                          {run.configSnapshot?.therapistLlmConfig?.model || 'gemini-2.5-flash'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          {run.configSnapshot?.cbcConfig?.enabled && (
                            <Chip label="CBC" size="small" color="primary" variant="outlined" sx={{ fontSize: '0.65rem', height: 20 }} />
                          )}
                          {run.configSnapshot?.parasocialConfig?.enabled && (
                            <Chip label="PS" size="small" color="secondary" variant="outlined" sx={{ fontSize: '0.65rem', height: 20 }} />
                          )}
                          {!run.configSnapshot?.cbcConfig?.enabled && !run.configSnapshot?.parasocialConfig?.enabled && (
                            <Typography variant="caption" color="text.secondary">-</Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>{run.turnsCount || 0}</TableCell>
                      <TableCell>
                        {run.metrics?.estimatedRealWorldDurationS
                          ? `~${Math.round(run.metrics.estimatedRealWorldDurationS / 60)} min`
                          : '-'}
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption">
                          {run.completionReason
                            ? run.completionReason.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
                            : '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={run.status}
                          size="small"
                          color={
                            run.status === 'completed' ? 'success' :
                            run.status === 'failed' ? 'error' :
                            run.status === 'running' ? 'primary' : 'default'
                          }
                        />
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSelectRun(run._id);
                          }}
                          disabled={isLoadingDetails}
                        >
                          View Details
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <TablePagination
                component="div"
                count={paginationInfo.total || 0}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[10, 20, 50]}
              />
            </TableContainer>
          )}
        </>
      )}

      {/* Export Modal */}
      {selectedRun && (
        <AdvancedSimulationExportModal
          open={exportModalOpen}
          onClose={() => setExportModalOpen(false)}
          runId={selectedRun._id}
          token={token}
          run={selectedRun}
        />
      )}
    </Box>
  );
};

export default AdvancedSimulationViewer;
